import React from 'react';
import { ArrowRight, PanelBottom, PanelTop } from 'lucide-react';
import {
  AppButton, AppCard, AppCardContent, AppCardDescription, AppCardHeader, AppCardTitle,
  Callout, Checkbox, FormField, Input, Separator, Textarea,
} from '../ui';
import ImageField from './ImageField';
import LinkListEditor from './LinkListEditor';
import CoursePicker from './CoursePicker';
import {
  brandNameDefault, defaultFooterLinks, defaultMoreLinks, defaultNavLinks, layoutHasOwnChrome,
} from './cmsSiteModel';

// ─── HeaderFooterTab — the website's header, menus and footer ────────────
//
// One place for everything that frames every page. It edits two stores —
// the customisation settings (menus, logos, which footer columns show) and
// the `global` page content (footer text, social links, column titles, the
// demo popup's heading) — which used to be two screens that each looked
// like the whole story.
//
// theme-3 draws its own header and footer from its own content, so for it
// this tab only points there.

const SOCIALS = [
  { key: 'socialFacebook', label: 'Facebook', placeholder: 'https://facebook.com/yourpage' },
  { key: 'socialInstagram', label: 'Instagram', placeholder: 'https://instagram.com/yourpage' },
  { key: 'socialLinkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/company/…' },
  { key: 'socialYoutube', label: 'YouTube', placeholder: 'https://youtube.com/@yourchannel' },
  { key: 'socialTwitter', label: 'X (Twitter)', placeholder: 'https://x.com/yourhandle' },
  { key: 'socialWhatsapp', label: 'WhatsApp', placeholder: '+91 98765 43210 or https://wa.me/…', classicOnly: true },
  { key: 'socialTelegram', label: 'Telegram', placeholder: 'https://t.me/yourchannel', classicOnly: true },
];

const COLUMN_TITLES = {
  default: { links: 'Quick Links', courses: 'Popular Courses', contact: 'Get In Touch' },
  'theme-2': { links: 'Explore', courses: 'Programs', contact: 'Contact' },
};

function LogoHeights({ values, onChange, limits }) {
  return (
    <div className="tw-grid tw-grid-cols-1 tw-gap-3 min-[480px]:tw-grid-cols-3">
      {[
        ['logoMaxHeightDesktop', 'Desktop', limits[0]],
        ['logoMaxHeightTablet', 'Tablet', limits[1]],
        ['logoMaxHeightMobile', 'Phone', limits[2]],
      ].map(([key, label, max]) => (
        <FormField key={key} label={`${label} height`}>
          <Input
            type="number"
            min={20}
            max={max}
            trailing="px"
            value={values[key]}
            onChange={(e) => onChange(key, Number(e.target.value))}
          />
        </FormField>
      ))}
    </div>
  );
}

function HeaderCard({ layoutPreset, navbar, setNav, imageSlot, global, setGlobal, errors }) {
  const classic = layoutPreset === 'default';
  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle><PanelTop size={16} /> Header</AppCardTitle>
        <AppCardDescription>The bar at the top of every page.</AppCardDescription>
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
        <div className="tw-grid tw-grid-cols-1 tw-gap-5 min-[721px]:tw-grid-cols-2">
          <ImageField
            label="Logo on the header bar"
            hint="Defaults to your logo from Settings → General."
            error={errors['navbar.logoScrolledBarExternalUrl']}
            {...imageSlot('navbar', 'logoScrolledBar')}
          />
          <ImageField
            label="Light logo over the hero"
            hint="Optional. A white or light version for the dark hero at the top of the home page."
            error={errors['navbar.logoOverHeroExternalUrl']}
            {...imageSlot('navbar', 'logoOverHero')}
          />
        </div>
        <LogoHeights values={navbar} onChange={setNav} limits={[120, 100, 80]} />

        <div className="tw-flex tw-flex-col tw-gap-2.5">
          <Checkbox
            label="Show the brand name next to the logo"
            checked={navbar.showBrandNameWithLogo ?? brandNameDefault(layoutPreset)}
            onChange={(e) => setNav('showBrandNameWithLogo', e.target.checked)}
          />
          <Checkbox
            label="Keep the header visible while scrolling"
            checked={navbar.stickyOnScroll}
            onChange={(e) => setNav('stickyOnScroll', e.target.checked)}
          />
        </div>

        <Separator />

        <div className="tw-grid tw-grid-cols-1 tw-gap-5 min-[721px]:tw-grid-cols-2">
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Checkbox
              label="Show a main button"
              description="Opens the demo form when it links to /contact."
              checked={navbar.showGetStarted}
              onChange={(e) => setNav('showGetStarted', e.target.checked)}
            />
            {navbar.showGetStarted && (
              <div className="tw-grid tw-grid-cols-2 tw-gap-3">
                <FormField label="Button text">
                  <Input value={navbar.getStartedText} onChange={(e) => setNav('getStartedText', e.target.value)} />
                </FormField>
                <FormField label="Links to">
                  <Input value={navbar.getStartedLink} onChange={(e) => setNav('getStartedLink', e.target.value)} placeholder="/contact" />
                </FormField>
                <Checkbox
                  size="sm"
                  label="Open in a new tab"
                  checked={navbar.getStartedOpenInNewTab}
                  onChange={(e) => setNav('getStartedOpenInNewTab', e.target.checked)}
                />
              </div>
            )}
          </div>
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Checkbox
              label="Show the “Book demo” button"
              description="Opens the demo request popup."
              checked={navbar.showBookDemo}
              onChange={(e) => setNav('showBookDemo', e.target.checked)}
            />
            {navbar.showBookDemo && (
              <FormField label="Button text">
                <Input value={navbar.bookDemoText} onChange={(e) => setNav('bookDemoText', e.target.value)} />
              </FormField>
            )}
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-1 tw-gap-4 min-[721px]:tw-grid-cols-2">
          <FormField label="Demo popup — heading" hint="To change the popup’s fields, turn on one of your forms in Tools → Enquiry Forms.">
            <Input
              placeholder={classic ? 'Book a free demo' : 'Book a demo'}
              value={global.demoModalTitle || ''}
              onChange={(e) => setGlobal('demoModalTitle', e.target.value)}
            />
          </FormField>
          <FormField label="Demo popup — text">
            <Input
              placeholder="Share your details and our team will reach out to schedule it."
              value={global.demoModalSubtitle || ''}
              onChange={(e) => setGlobal('demoModalSubtitle', e.target.value)}
            />
          </FormField>
        </div>

        <Separator />

        <LinkListEditor
          title="Menu"
          links={navbar.navLinks}
          onChange={(v) => setNav('navLinks', v)}
          onReset={() => setNav('navLinks', defaultNavLinks(global))}
          error={errors['navbar.navLinks']}
        />

        {classic && (
          <>
            <Checkbox
              label="Show a “More” menu"
              description="A dropdown after the menu for secondary pages."
              checked={!navbar.hideMoreDropdown}
              onChange={(e) => setNav('hideMoreDropdown', !e.target.checked)}
            />
            {!navbar.hideMoreDropdown && (
              <LinkListEditor
                title="“More” menu"
                withIcon
                links={navbar.moreLinks}
                onChange={(v) => setNav('moreLinks', v)}
                onReset={() => setNav('moreLinks', defaultMoreLinks(global))}
                error={errors['navbar.moreLinks']}
              />
            )}
          </>
        )}
      </AppCardContent>
    </AppCard>
  );
}

function FooterCard({
  layoutPreset, footer, setFoot, imageSlot, global, setGlobal, courses, errors, onEditContact,
}) {
  const classic = layoutPreset === 'default';
  const titles = COLUMN_TITLES[layoutPreset] || COLUMN_TITLES.default;
  const year = new Date().getFullYear();

  return (
    <AppCard>
      <AppCardHeader>
        <AppCardTitle><PanelBottom size={16} /> Footer</AppCardTitle>
        <AppCardDescription>The block at the bottom of every page.</AppCardDescription>
      </AppCardHeader>
      <AppCardContent className="tw-flex tw-flex-col tw-gap-5">
        <div className="tw-grid tw-grid-cols-1 tw-gap-5 min-[721px]:tw-grid-cols-2">
          <ImageField
            label="Footer logo"
            hint="For the dark footer. Without one, your main logo is shown on a light plate."
            error={errors['footer.logoBrandExternalUrl']}
            {...imageSlot('footer', 'logoBrand')}
          />
          {classic && (
            <ImageField
              label="Small logo above the copyright line"
              hint="Optional — an accreditation or partner mark, for example."
              error={errors['footer.logoBottomBarExternalUrl']}
              {...imageSlot('footer', 'logoBottomBar')}
            />
          )}
        </div>
        <LogoHeights values={footer} onChange={setFoot} limits={[200, 150, 100]} />
        <Checkbox
          label="Show the brand name next to the footer logo"
          checked={footer.showBrandNameWithLogo ?? brandNameDefault(layoutPreset)}
          onChange={(e) => setFoot('showBrandNameWithLogo', e.target.checked)}
        />
        <FormField label="About text" hint="A sentence or two under the logo.">
          <Textarea
            rows={2}
            placeholder="Empowering students with industry-ready skills through expert-led training…"
            value={global.footerAbout || ''}
            onChange={(e) => setGlobal('footerAbout', e.target.value)}
          />
        </FormField>
        {classic && (
          <div className="tw-grid tw-grid-cols-1 tw-items-end tw-gap-4 min-[721px]:tw-grid-cols-2">
            <Checkbox
              label="Show a “Talk to a counsellor” button"
              description="Opens the demo request popup."
              checked={global.showFooterCta !== false}
              onChange={(e) => setGlobal('showFooterCta', e.target.checked)}
            />
            {global.showFooterCta !== false && (
              <FormField label="Button text">
                <Input
                  placeholder="Talk to a Counsellor"
                  value={global.footerCtaText || ''}
                  onChange={(e) => setGlobal('footerCtaText', e.target.value)}
                />
              </FormField>
            )}
          </div>
        )}

        <Separator />

        <section aria-label="Links column" className="tw-flex tw-flex-col tw-gap-3">
          <Checkbox
            label="Show a links column"
            checked={!footer.hideQuickLinks}
            onChange={(e) => setFoot('hideQuickLinks', !e.target.checked)}
          />
          {!footer.hideQuickLinks && (
            <>
              <FormField label="Column title">
                <Input placeholder={titles.links} value={global.footerLinksTitle || ''} onChange={(e) => setGlobal('footerLinksTitle', e.target.value)} />
              </FormField>
              <LinkListEditor
                title="Links"
                links={footer.quickLinks}
                onChange={(v) => setFoot('quickLinks', v)}
                onReset={() => setFoot('quickLinks', defaultFooterLinks(global))}
                error={errors['footer.quickLinks']}
              />
            </>
          )}
        </section>

        <Separator />

        <section aria-label="Courses column" className="tw-flex tw-flex-col tw-gap-3">
          <Checkbox
            label="Show a courses column"
            checked={!footer.hidePopularCourses}
            onChange={(e) => setFoot('hidePopularCourses', !e.target.checked)}
          />
          {!footer.hidePopularCourses && (
            <div className="tw-grid tw-grid-cols-1 tw-gap-4 min-[721px]:tw-grid-cols-2">
              <FormField label="Column title">
                <Input placeholder={titles.courses} value={global.footerCoursesTitle || ''} onChange={(e) => setGlobal('footerCoursesTitle', e.target.value)} />
              </FormField>
              <CoursePicker
                label="Courses to list"
                hint="Up to five. With none picked, your newest courses are listed."
                max={5}
                courses={courses}
                value={global.footerCourses}
                onChange={(v) => setGlobal('footerCourses', v)}
              />
            </div>
          )}
        </section>

        <Separator />

        <section aria-label="Contact column" className="tw-flex tw-flex-col tw-gap-3">
          <Checkbox
            label="Show a contact column"
            checked={!footer.hideContactInfo}
            onChange={(e) => setFoot('hideContactInfo', !e.target.checked)}
          />
          {!footer.hideContactInfo && (
            <div className="tw-grid tw-grid-cols-1 tw-items-end tw-gap-4 min-[721px]:tw-grid-cols-2">
              <FormField label="Column title">
                <Input placeholder={titles.contact} value={global.footerContactTitle || ''} onChange={(e) => setGlobal('footerContactTitle', e.target.value)} />
              </FormField>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm tw-text-content-muted">
                The address, phone, email and hours come from the Contact page.
                <AppButton size="sm" variant="ghost" onClick={onEditContact}>
                  Edit contact details <ArrowRight size={16} />
                </AppButton>
              </div>
            </div>
          )}
        </section>

        <Separator />

        <section aria-label="Social links" className="tw-flex tw-flex-col tw-gap-3">
          <Checkbox
            label="Show social media icons"
            description="An icon appears for each profile you fill in."
            checked={!footer.hideSocialLinks}
            onChange={(e) => setFoot('hideSocialLinks', !e.target.checked)}
          />
          {!footer.hideSocialLinks && (
            <div className="tw-grid tw-grid-cols-1 tw-gap-4 min-[721px]:tw-grid-cols-2">
              {SOCIALS.filter((s) => classic || !s.classicOnly).map((s) => (
                <FormField key={s.key} label={s.label}>
                  <Input type="url" placeholder={s.placeholder} value={global[s.key] || ''} onChange={(e) => setGlobal(s.key, e.target.value)} />
                </FormField>
              ))}
            </div>
          )}
        </section>

        <Separator />

        <FormField label="Copyright line" hint="Leave blank for the standard line.">
          <Input
            placeholder={`© ${year} Your Institute. All rights reserved.`}
            value={footer.copyrightText}
            onChange={(e) => setFoot('copyrightText', e.target.value)}
          />
        </FormField>
      </AppCardContent>
    </AppCard>
  );
}

export default function HeaderFooterTab({
  layoutPreset, layoutName, customization, setCustomizationField, imageSlot,
  content, setPageField, courses, errors, onGoToPages,
}) {
  if (layoutHasOwnChrome(layoutPreset)) {
    return (
      <Callout tone="info">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
          <span>
            The {layoutName} layout draws its own header, menu and footer. Edit them on the Global page.
          </span>
          <AppButton size="sm" variant="outline" onClick={() => onGoToPages({ page: 't3_global' })}>
            Open Pages → Global <ArrowRight size={16} />
          </AppButton>
        </div>
      </Callout>
    );
  }

  const global = content.global || {};
  const setGlobal = (key, value) => setPageField('global', key, value);

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <HeaderCard
        layoutPreset={layoutPreset}
        navbar={customization.navbar}
        setNav={(key, value) => setCustomizationField('navbar', key, value)}
        imageSlot={imageSlot}
        global={global}
        setGlobal={setGlobal}
        errors={errors}
      />
      <FooterCard
        layoutPreset={layoutPreset}
        footer={customization.footer}
        setFoot={(key, value) => setCustomizationField('footer', key, value)}
        imageSlot={imageSlot}
        global={global}
        setGlobal={setGlobal}
        courses={courses}
        errors={errors}
        onEditContact={() => onGoToPages({ page: 'contact', section: 'details' })}
      />
    </div>
  );
}
